/**
 * @auctogon/ui/web — Web-only (DOM) component entry.
 *
 * Components here use DOM types (HTMLInputElement, ClipboardEvent, focus(), …)
 * and must NOT be imported by React Native consumers. The package root
 * (`@auctogon/ui`) stays RN-safe; web apps import DOM components from here.
 *
 * Form primitives shared between admin-web and bidder-web per ADR 016 also
 * live here. `applyServerErrors` is React-only (RHF) and not DOM-bound, but
 * co-locates with `FormField` since both are part of the same form pattern.
 */
export * from './hooks/useFocusTrap';
export * from './hooks/useTopmostEscape';
// Named, not `export *`: the module's test reset stays off the production barrel
// (tests import it from `@auctogon/ui/hooks/bodyScrollLock.test-helpers`).
export { acquireBodyScrollLock, releaseBodyScrollLock, useBodyScrollLock } from './hooks/bodyScrollLock';
export * from './hooks/useOutsideClick';
export * from './hooks/useBackdropDismiss';
export * from './hooks/nonModalDismissTarget';
export * from './hooks/useViewerTimezone';
export * from './hooks/useMediaQuery';
export * from './hooks/useVisualViewportBox';
export * from './hooks/usePrefersReducedMotion';
export * from './hooks/submitOnEnter';
export * from './hooks/useRevalidateAfterSubmit';
export * from './hooks/useStoredPreference';
export * from './hooks/useNotificationSound';
export * from './components/AuctogonLogo';
export * from './components/OTPInput';
export * from './components/Spinner';
export * from './components/Skeleton';
export * from './components/RadioButton';
export * from './components/Checkbox';
export * from './components/PhoneInput';
export * from './components/Card';
export {
    Button,
    type ButtonProps,
    type ButtonDialect,
    type ButtonBrand,
    type ButtonLinkProps,
} from './components/Button';
export { Input, type InputProps, type InputDialect } from './components/Input';
export * from './components/CountDot';
export * from './components/Toaster';
export { StepBar, type StepBarProps, type StepBarStep, type StepBarDialect } from './components/StepBar';
export {
    ConnectionHealthBar,
    type ConnectionHealthBarProps,
} from './components/ConnectionHealthBar';
export { default as EmptyState, type EmptyStateProps, type EmptyStateDialect } from './components/EmptyState';
export { Alert, type AlertProps, type AlertTone, type AlertDialect } from './components/Alert';
export { Avatar, type AvatarProps, type AvatarShape, type AvatarDialect } from './components/Avatar';
export {
    Badge,
    badgeShellClassName,
    type BadgeProps,
    type BadgeDialect,
    type BadgeShellTone,
} from './components/Badge';
export { default as Modal, type ModalProps, type ModalDialect } from './components/Modal';
export {
    default as ConfirmDialogContent,
    useConfirmAction,
    CONFIRM_FALLBACK_ERROR,
    type ConfirmDialogContentProps,
    type ConfirmDialogDialect,
    type ConfirmDialogVariantClasses,
    type UseConfirmActionOptions,
    type ConfirmActionState,
} from './components/ConfirmDialog';
export {
    default as ReorderableList,
    type ReorderableListProps,
    type ReorderableListItem,
    type ReorderableListDialect,
    type ReorderDropPosition,
} from './components/ReorderableList';
export * from './components/FormField';
export { FIELD_ERROR_CLASS, FIELD_ERROR_TEXT, FIELD_HINT_CLASS, AdminDialectContext, AdminDialectProvider, FieldErrorText } from './components/FieldErrorText';
export * from './components/Select';
export * from './components/SearchInput';
export * from './components/AddressAutocomplete';
export * from './components/AddressFormFields';
export * from './hooks/focus-address-part';
export * from './hooks/useLocationAddressFields';
// BL-2459: the one Finix secure tokenization form (it holds the one finix.js loader).
export {
    SecureCardForm,
    SECURE_FORM_MESSAGES,
    type SecureCardFormProps,
    type SecureCardFormBaseProps,
    type SecureCardFormOwnButtonProps,
    type SecureCardFormExternalSubmitProps,
    type SecureCardFormHandle,
    type SecureFormMethod,
    type SecureFormSubmitColors,
    type SecureFormTokenMeta,
} from './components/SecureCardForm';
export { US_STATES, type StateOption } from './components/address-constants';
export * from './isomorphic/forms/apply-server-errors';
export * from './isomorphic/forms/option-handle';
export * from './isomorphic/popover/popover-position';

// Time display primitives (BL-596) — formatting + zone logic from
// @auctogon/time, components per platform (these are the web set).
export * from './components/EventTime';
export * from './components/RelativeTime';
export * from './components/LiveCountdown';
